import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../lib/api';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Gift, Users, Copy, Check, Share2, Trophy, 
  Wallet, ChevronRight, Sparkles, ArrowRight 
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

export default function Referral() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [referralData, setReferralData] = useState(null);
  const [history, setHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  
  // Check for referral code in URL (for new users)
  const refCode = searchParams.get('ref');

  useEffect(() => {
    if (user) {
      fetchReferralData();
      fetchHistory();
    }
    fetchLeaderboard();
    fetchStats();
  }, [user]);

  // Auto-apply referral code for new users
  useEffect(() => {
    if (refCode && user && !referralData?.referral_code) {
      applyReferralCode(refCode);
    }
  }, [refCode, user]);

  const fetchReferralData = async () => {
    try {
      const res = await API.get('/referral/my-code');
      setReferralData(res.data);
    } catch (err) {
      console.error('Failed to fetch referral data:', err);
    }
    setLoading(false);
  };

  const fetchHistory = async () => {
    try {
      const res = await API.get('/referral/history');
      setHistory(res.data.history);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await API.get('/referral/leaderboard');
      setLeaderboard(res.data.leaderboard);
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await API.get('/referral/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const applyReferralCode = async (code) => {
    try {
      const res = await API.post('/referral/apply', { referral_code: code });
      toast.success(res.data.message);
      fetchReferralData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply referral code');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareReferral = () => {
    if (navigator.share && referralData) {
      navigator.share({
        title: 'Join AutoTrust Ghana',
        text: `Join Ghana's most trusted car marketplace and we both get GHS 10 credit! Use my referral code: ${referralData.referral_code}`,
        url: referralData.referral_link
      }).catch(() => {});
    } else {
      copyToClipboard(referralData?.referral_link);
    }
  };

  // If not logged in, show public referral page
  if (!user) {
    return (
      <main data-testid="referral-page" className="min-h-screen bg-background">
        <SEO 
          title="Refer Friends & Earn Rewards"
          description="Invite friends to AutoTrust Ghana and earn GHS 10 credit for each successful referral. Join the community and start earning today!"
        />
        
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gold/10 mb-4">
                <Gift className="w-10 h-10 text-gold" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold">
                Earn <span className="text-gold">GHS 10</span> For Every Friend
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Invite friends to AutoTrust Ghana. When they make their first listing or purchase, 
                you both earn GHS 10 credit!
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                <Link to="/register">
                  <Button size="lg" className="bg-gold text-black hover:brightness-110 gap-2">
                    <Sparkles className="w-5 h-5" />
                    Join Now & Get Your Code
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="outline" className="gap-2">
                    Already have an account?
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* How it works */}
            <div className="grid md:grid-cols-3 gap-6 mt-16">
              {[
                { icon: Share2, title: 'Share Your Link', desc: 'Get your unique referral link and share it with friends' },
                { icon: Users, title: 'Friends Join', desc: 'When friends sign up and complete their first transaction' },
                { icon: Wallet, title: 'Both Earn GHS 10', desc: 'You and your friend both get GHS 10 credit instantly' }
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.1 }}
                  className="bg-card rounded-xl border border-border/50 p-6 text-center"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gold/10 mb-4">
                    <step.icon className="w-6 h-6 text-gold" />
                  </div>
                  <h3 className="font-semibold mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.desc}</p>
                </motion.div>
              ))}
            </div>

            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-12">
                <div className="bg-card rounded-xl border border-border/50 p-4">
                  <div className="text-2xl font-bold text-gold">{stats.total_successful_referrals?.toLocaleString() || 0}</div>
                  <div className="text-sm text-muted-foreground">Successful Referrals</div>
                </div>
                <div className="bg-card rounded-xl border border-border/50 p-4">
                  <div className="text-2xl font-bold text-gold">GHS {stats.total_rewards_distributed?.toLocaleString() || 0}</div>
                  <div className="text-sm text-muted-foreground">Rewards Distributed</div>
                </div>
                <div className="bg-card rounded-xl border border-border/50 p-4 col-span-2 md:col-span-1">
                  <div className="text-2xl font-bold text-gold">GHS {stats.reward_per_referral || 20}</div>
                  <div className="text-sm text-muted-foreground">Per Referral (Both Parties)</div>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
    );
  }

  // Logged in user view
  return (
    <main data-testid="referral-page" className="min-h-screen bg-background py-8 px-4">
      <SEO 
        title="My Referrals"
        description="Track your referrals and earnings on AutoTrust Ghana."
      />
      
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-2xl md:text-3xl font-bold">Referral Program</h1>
          <p className="text-muted-foreground">Invite friends and earn GHS 10 for each successful referral</p>
        </div>

        {/* Main Stats Cards */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-gold/20 to-gold/5 border-gold/20">
            <CardHeader className="pb-2">
              <CardDescription>Available Credit</CardDescription>
              <CardTitle className="text-3xl text-gold">
                GHS {referralData?.available_credit?.toFixed(2) || '0.00'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">Use for listing fees or purchases</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Successful Referrals</CardDescription>
              <CardTitle className="text-3xl">{referralData?.successful_referrals || 0}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">
                {referralData?.pending_referrals || 0} pending
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Earned</CardDescription>
              <CardTitle className="text-3xl">GHS {referralData?.total_earned?.toFixed(2) || '0.00'}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">From all referrals</p>
            </CardContent>
          </Card>
        </div>

        {/* Referral Link */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-gold" />
              Your Referral Link
            </CardTitle>
            <CardDescription>
              Share this link with friends. You'll both earn GHS 10 when they complete their first transaction.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input 
                value={referralData?.referral_link || ''} 
                readOnly 
                className="font-mono text-sm"
              />
              <Button 
                variant="outline" 
                onClick={() => copyToClipboard(referralData?.referral_link)}
                className="shrink-0"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="bg-muted rounded-lg px-4 py-2">
                <span className="text-xs text-muted-foreground">Your Code:</span>
                <span className="ml-2 font-mono font-bold text-gold">{referralData?.referral_code}</span>
              </div>
              <Button onClick={shareReferral} className="bg-gold text-black hover:brightness-110 gap-2">
                <Share2 className="w-4 h-4" />
                Share
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* History and Leaderboard */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Referral History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-gold" />
                Referral History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No referrals yet</p>
                  <p className="text-xs">Share your link to start earning!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.slice(0, 5).map((ref, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                      <div>
                        <p className="font-medium">{ref.referee_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(ref.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge variant={ref.status === 'completed' ? 'default' : 'secondary'}>
                          {ref.status}
                        </Badge>
                        {ref.status === 'completed' && (
                          <p className="text-xs text-gold mt-1">+GHS {ref.reward_amount}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Leaderboard */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-gold" />
                Top Referrers
              </CardTitle>
            </CardHeader>
            <CardContent>
              {leaderboard.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Trophy className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Be the first on the leaderboard!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {leaderboard.slice(0, 5).map((entry, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          i === 0 ? 'bg-gold text-black' :
                          i === 1 ? 'bg-gray-400 text-white' :
                          i === 2 ? 'bg-amber-600 text-white' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {entry.rank}
                        </span>
                        <span className="font-medium">{entry.name}</span>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{entry.referrals} referrals</p>
                        <p className="text-xs text-gold">GHS {entry.earned}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* How it works */}
        <Card>
          <CardHeader>
            <CardTitle>How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { step: '1', title: 'Share Your Link', desc: 'Send your unique referral link to friends via WhatsApp, SMS, or social media' },
                { step: '2', title: 'Friend Signs Up', desc: 'Your friend creates an account using your link and completes their first listing or purchase' },
                { step: '3', title: 'Both Earn GHS 10', desc: 'Once verified, you both receive GHS 10 credit to use on the platform' }
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-gold/10 text-gold flex items-center justify-center font-bold shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">{item.title}</h4>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
